import axios from 'axios';

// Use relative URLs - nginx proxies /api to the backend
const api = axios.create({
  baseURL: '',
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Only set Content-Type for non-FormData requests
  if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

// Handle 401 responses (token expired or invalid)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      
      // Only redirect if not already on login page and not during initial validation
      const isLoginPage = window.location.pathname.includes('/login');
      const isAuthCheck = error.config?.url?.includes('/api/auth/me');
      
      if (!isLoginPage && !isAuthCheck) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  register: async (username, email, password) => {
    const response = await api.post('/api/auth/register', {
      username,
      email,
      password,
    });
    return response.data;
  },

  login: async (username, password, rememberMe = false, totpCode = null) => {
    const response = await api.post('/api/auth/login', {
      username,
      password,
      remember_me: rememberMe,
      totp_code: totpCode,
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  updateSettings: async (data) => {
    const response = await api.put('/api/auth/me/settings', data);
    return response.data;
  },

  // Password reset
  forgotPassword: async (email) => {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/api/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  // 2FA
  setup2FA: async () => {
    const response = await api.post('/api/auth/2fa/setup');
    return response.data;
  },

  verify2FA: async (totpCode) => {
    const response = await api.post('/api/auth/2fa/verify', { totp_code: totpCode });
    return response.data;
  },

  disable2FA: async (totpCode) => {
    const response = await api.post('/api/auth/2fa/disable', { totp_code: totpCode });
    return response.data;
  },

  getEmailStatus: async () => {
    const response = await api.get('/api/auth/email-status');
    return response.data;
  },
};

export const expenseService = {
  // CRUD
  // idempotencyKey: client-generated id so retried uploads (offline sync)
  // never create duplicates — the server returns the existing expense instead
  createExpense: async (data, file, idempotencyKey = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('date', data.date);
    formData.append('amount', data.amount);
    formData.append('currency', data.currency || 'EUR');
    if (data.description) formData.append('description', data.description);
    if (data.merchant) formData.append('merchant', data.merchant);
    if (data.category) formData.append('category', data.category);
    if (data.attendees) formData.append('attendees', data.attendees);
    if (data.client) formData.append('client', data.client);
    if (data.trip_id) formData.append('trip_id', data.trip_id);
    if (idempotencyKey) formData.append('idempotency_key', idempotencyKey);

    const response = await api.post('/api/expenses/', formData);
    return response.data;
  },

  getExpenses: async (params = {}) => {
    const response = await api.get('/api/expenses/', { params });
    return response.data;
  },

  getExpense: async (id) => {
    const response = await api.get(`/api/expenses/${id}`);
    return response.data;
  },

  updateExpense: async (id, data) => {
    const response = await api.put(`/api/expenses/${id}`, data);
    return response.data;
  },

  bulkUpdateExpenses: async (ids, fields) => {
    const response = await api.put('/api/expenses/bulk', { ids, ...fields });
    return response.data;
  },

  deleteExpense: async (id) => {
    const response = await api.delete(`/api/expenses/${id}`);
    return response.data;
  },

  // Dashboard
  getDashboardStats: async () => {
    const response = await api.get('/api/expenses/dashboard/stats');
    return response.data;
  },

  // Field suggestions (for autocomplete)
  getFieldSuggestions: async () => {
    const response = await api.get('/api/expenses/suggestions/fields');
    return response.data;
  },

  // Currency conversion
  getCurrencyRates: async (baseCurrency = 'EUR') => {
    const response = await api.get(`/api/expenses/currencies/rates?base_currency=${baseCurrency}`);
    return response.data;
  },

  convertCurrency: async (amount, fromCurrency, toCurrency) => {
    const response = await api.post('/api/expenses/convert', null, {
      params: {
        amount,
        from_currency: fromCurrency,
        to_currency: toCurrency,
      }
    });
    return response.data;
  },

  // Export PDF
  exportToPdf: async (expenseIds) => {
    const response = await api.post('/api/expenses/export/pdf', expenseIds);
    return response.data;
  },

  // Regenerate missing thumbnails
  regenerateThumbnails: async (force = false) => {
    const response = await api.post(`/api/expenses/regenerate-thumbnails?force=${force}`);
    return response.data;
  },

  // Download a protected file (e.g. an exported PDF) as a Blob
  fetchFileBlob: async (path) => {
    const response = await api.get(path, { responseType: 'blob' });
    return response.data;
  },

  // Helper for file URLs - adds auth token for secure access
  getFileUrl: (path) => {
    if (!path) return null;
    const token = localStorage.getItem('auth_token');
    if (!token) return null;
    return `${path}?token=${encodeURIComponent(token)}`;
  },
};

export const profileService = {
  getProfile: async () => {
    const response = await api.get('/api/auth/me/profile');
    return response.data;
  },
  updateProfile: async (data) => {
    const response = await api.put('/api/auth/me/profile', data);
    return response.data;
  },
};

export const clientService = {
  getClients: async () => {
    const response = await api.get('/api/clients/');
    return response.data;
  },
  createClient: async (data) => {
    const response = await api.post('/api/clients/', data);
    return response.data;
  },
  updateClient: async (id, data) => {
    const response = await api.put(`/api/clients/${id}`, data);
    return response.data;
  },
  deleteClient: async (id) => {
    await api.delete(`/api/clients/${id}`);
  },
  addLocation: async (clientId, data) => {
    const response = await api.post(`/api/clients/${clientId}/locations`, data);
    return response.data;
  },
  updateLocation: async (clientId, locationId, data) => {
    const response = await api.put(`/api/clients/${clientId}/locations/${locationId}`, data);
    return response.data;
  },
  deleteLocation: async (clientId, locationId) => {
    await api.delete(`/api/clients/${clientId}/locations/${locationId}`);
  },
};

export const tripService = {
  getTrips: async () => {
    const response = await api.get('/api/trips/');
    return response.data;
  },
  getTrip: async (id) => {
    const response = await api.get(`/api/trips/${id}`);
    return response.data;
  },
  createTrip: async (data) => {
    const response = await api.post('/api/trips/', data);
    return response.data;
  },
  updateTrip: async (id, data) => {
    const response = await api.put(`/api/trips/${id}`, data);
    return response.data;
  },
  deleteTrip: async (id) => {
    await api.delete(`/api/trips/${id}`);
  },
  getCalendar: async (year, month) => {
    const response = await api.get('/api/trips/calendar', { params: { year, month } });
    return response.data;
  },
  getComputedCosts: async (id) => {
    const response = await api.get(`/api/trips/${id}/costs`);
    return response.data;
  },
  // Returns the travel allowance PDF as a Blob (see utils/pdfDelivery to open/share it)
  generatePdf: async (id, costOverrides = {}) => {
    const response = await api.post(
      `/api/trips/${id}/pdf`,
      costOverrides,
      { responseType: 'blob' }
    );
    return new Blob([response.data], { type: 'application/pdf' });
  },
};

export default api;
